import React, { useEffect, useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material"
import { Link, useNavigate } from "react-router-dom"
import ForwardIcon from "@mui/icons-material/Forward"
import DownloadIcon from "@mui/icons-material/Download"
import DeleteIcon from "@mui/icons-material/Delete"
import api from "../../../utils/api"

interface EventSummary {
  id: string
  name: string
  description: string
  adminLimit: number
  numVoters: number
  //passwordReq: boolean;
  endDate: { _seconds: number; _nanoseconds: number }
  startDate: { _seconds: number; _nanoseconds: number }
  archiveStatus: boolean
}

const convertToDate = (rawDate: {
  _seconds: number
  _nanoseconds: number
}): Date => {
  return new Date(rawDate._seconds * 1000)
}

const ViewArchivesPage: React.FC = () => {
  const [events, setEvents] = useState<EventSummary[]>([])
  const navigate = useNavigate()

  const [eventId, setEventId] = useState("")
  const [isDownloading, setIsDownloading] = useState(false)

  //Testing
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)
  const handleDelete = (eventId: string) => {
    if (!eventId) {
      return
    }

    setEventId(eventId)
    setIsDeleteConfirmationOpen(true)
  }
  const handleConfirmDelete = async () => {
    setIsDeleteConfirmationOpen(false)

    if (!eventId) {
      return
    }

    try {
      await api.delete(`/admin/events/${eventId}`)

      navigate("/admin/dashboard", {
        state: { notification: "Event successfully deleted!" },
      })
    } catch (error) {
      console.error("Error deleting event:", error)
    }
  }

  const downloadEventData = async (eventId: string) => {
    try {
      setIsDownloading(true);
      
      // Show download message using browser's native alert
      //alert('Download started. This might take a few seconds...');
      
      // Fetch basic event information
      const eventResponse = await api.get(`/admin/events/${eventId}`);
      const eventData = eventResponse.data;
      
      // Fetch voting instances for the event
      const instancesResponse = await api.get(`/admin/events/${eventId}/voting-instances`);
      const instancesData = instancesResponse.data as any[];
      
      // Fetch voter accounts for the event
      const votersResponse = await api.get(`/admin/events/${eventId}/voters`);
      const votersData = votersResponse.data as any[];

      // Structured results data for CSV
      let formattedResults: any[] = [];
      
      // Collect detailed question and answer data for each instance
      for (const instance of instancesData) {
        try {
          // Use the new comprehensive endpoint that includes questions, answers, and vote counts
          // Try both endpoint variations to ensure compatibility
          // Define proper interface for instanceData
          interface InstanceData {
            questions?: any[];
            answers?: any[];
            voteCounts?: Record<string, Record<string, number>>;
            totalVotes?: number;
          }
          
          let instanceData: InstanceData = {
            questions: [],
            answers: [],
            voteCounts: {},
            totalVotes: 0
          };
          
          try {
            // SIMPLIFIED APPROACH: Try to get at least basic instance information
            console.log('Attempting to fetch instance details for', instance.id);

            try {
              // Try the original comprehensive endpoint first
              const instanceDataResponse = await api.get(`/admin/question/instance/${instance.id}/data`);
              instanceData = instanceDataResponse.data || instanceData;
              console.log('Successfully fetched data from comprehensive endpoint');
            } catch (error) {
              console.warn('Comprehensive endpoint failed, using placeholder data');
              
              // If that fails, use placeholder data for the instance
              // This at least allows the CSV to be generated with event and instance info, just without voting results
              console.log('Using placeholder data for voting results');
              
              // Try to get basic instance info for a better placeholder
              try {
                // This is a common endpoint pattern that might exist
                const instanceResponse = await api.get(`/admin/events/${eventId}/instances/${instance.id}`);
                if (instanceResponse.data) {
                  // If we got some data, create a synthetic question and option
                  const placeholderQuestion = {
                    id: `placeholder_question_${instance.id}`,
                    description: `Questions for ${instance.description || 'this instance'}`,
                    questionType: 'multiple-choice'
                  };
                  
                  const placeholderAnswer = {
                    id: `placeholder_option_${instance.id}`,
                    questionId: placeholderQuestion.id,
                    option: 'Data unavailable in production environment',
                    optionNum: 1
                  };
                  
                  // Create placeholder vote count
                  const placeholderVoteCounts: Record<string, Record<string, number>> = {};
                  placeholderVoteCounts[placeholderQuestion.id] = {};
                  placeholderVoteCounts[placeholderQuestion.id][placeholderAnswer.id] = 0;
                  
                  instanceData = {
                    questions: [placeholderQuestion],
                    answers: [placeholderAnswer],
                    voteCounts: placeholderVoteCounts,
                    totalVotes: 0
                  };
                }
              } catch (instanceError) {
                console.warn('Could not fetch instance details, using minimal placeholder');
                // Create minimal placeholder data if we couldn't even get instance info
                const placeholderQuestion = {
                  id: `placeholder_${instance.id}`,
                  description: 'Voting results unavailable',
                  questionType: 'multiple-choice'
                };
                
                const placeholderAnswer = {
                  id: `placeholder_option_${instance.id}`,
                  questionId: placeholderQuestion.id,
                  option: 'Data unavailable in production',
                  optionNum: 1
                };
                
                const placeholderVoteCounts: Record<string, Record<string, number>> = {};
                placeholderVoteCounts[placeholderQuestion.id] = {};
                placeholderVoteCounts[placeholderQuestion.id][placeholderAnswer.id] = 0;
                
                instanceData = {
                  questions: [placeholderQuestion],
                  answers: [placeholderAnswer],
                  voteCounts: placeholderVoteCounts,
                  totalVotes: 0
                };
              }
            }
          } catch (error) {
            console.error('Failed to get any instance data:', error);
          }
          
          const { questions, answers, voteCounts, totalVotes } = instanceData;
          
          if (questions && Array.isArray(questions) && answers && Array.isArray(answers)) {
            // Process each question
            questions.forEach((question: any) => {
              const questionId = question.id;
              
              // Get options for this question
              const questionOptions = answers.filter((answer: any) => answer.questionId === questionId);
              
              // Calculate total votes for this question
              let questionTotalVotes = 0;
              if (voteCounts && voteCounts[questionId]) {
                // Safely sum up all vote counts for this question
                Object.keys(voteCounts[questionId]).forEach(optionId => {
                  const count = voteCounts[questionId][optionId];
                  questionTotalVotes += typeof count === 'number' ? count : 0;
                });
              }
              
              // Add each option as a result entry
              questionOptions.forEach((option: any) => {
                const optionVotes = 
                  (voteCounts && 
                   voteCounts[questionId] && 
                   voteCounts[questionId][option.id]) ? 
                  voteCounts[questionId][option.id] : 0;
                
                formattedResults.push({
                  instanceDescription: instance.description,
                  instanceId: instance.id,
                  questionId: questionId,
                  questionText: question.description,
                  optionText: option.option,
                  optionId: option.id,
                  votes: optionVotes,
                  totalVotes: questionTotalVotes
                });
              });
            });
          }
        } catch (error) {
          console.error(`Error fetching data for instance ${instance.id}:`, error);
        }
      }

      // Create CSV content with sections
      let csvContent: string[] = [];
      
      // Helper function to escape CSV values
      const escapeCSV = (value: any): string => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // If the value contains commas, quotes, or newlines, wrap it in quotes and escape quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };
      
      // 1. EVENT INFORMATION SECTION
      csvContent.push('=== EVENT INFORMATION ===');
      
      // Get all keys from the event data for the header
      const eventKeys = Object.keys(eventData).filter(key => 
        typeof eventData[key] !== 'object' || eventData[key] === null
      );
      
      // Add event data header row
      csvContent.push(eventKeys.map(key => escapeCSV(key)).join(','));
      
      // Add event data row
      csvContent.push(eventKeys.map(key => escapeCSV(eventData[key])).join(','));
      
      // Add spacer
      csvContent.push('');
      csvContent.push('');
      
      // 2. VOTING INSTANCES SECTION
      csvContent.push('=== VOTING INSTANCES ===');
      
      if (instancesData.length > 0) {
        // Get all unique keys from all instances
        const instanceKeys = Array.from(new Set(
          instancesData.flatMap((instance: any) => Object.keys(instance))
        )).filter(key => key !== 'questions' && key !== 'id' && key !== 'eventId'); // Exclude nested objects and id/eventId
        
        // Create a custom header with descriptive column names - using 'Description' as requested
        const customInstanceHeader = ['Description', ...instanceKeys.filter(key => key !== 'description')];
        csvContent.push(customInstanceHeader.map(key => escapeCSV(key)).join(','));
        
        // Add instance data rows
        instancesData.forEach((instance: any) => {
          // Start with the instance description
          const instanceDescription = instance.description || 'Unnamed Instance';
          const rowValues = [instanceDescription];
          
          // Add the rest of the fields (except for description since we already used it)
          instanceKeys.forEach(key => {
            if (key !== 'description') {
              const value = instance[key as keyof typeof instance];
              if (value && typeof value === 'object' && '_seconds' in value) {
                // Format timestamp
                rowValues.push(escapeCSV(new Date((value as any)._seconds * 1000).toISOString()));
              } else {
                rowValues.push(escapeCSV(value));
              }
            }
          });
          
          csvContent.push(rowValues.join(','));
        });
      } else {
        csvContent.push('No voting instances found');
      }

      // Add spacer
      csvContent.push('');
      csvContent.push('');
      
      // 3. VOTER ACCOUNTS SECTION
      csvContent.push('=== VOTER ACCOUNTS ===');
      
      if (votersData.length > 0) {
        // Get all unique keys from all voters, excluding the id field
        const voterKeys = Array.from(new Set(
          votersData.flatMap((voter: any) => Object.keys(voter))
        )).filter(key => key !== 'id'); // Remove the id column
        
        // Add voter header row
        csvContent.push(voterKeys.map(key => escapeCSV(key)).join(','));
        
        // Add voter data rows
        votersData.forEach((voter: any) => {
          csvContent.push(voterKeys.map(key => escapeCSV(voter[key as keyof typeof voter])).join(','));
        });
      } else {
        csvContent.push('No voter accounts found');
      }
      
      // Add spacer
      csvContent.push('');
      csvContent.push('');
      
      // 4. RESULTS SECTION (with detailed question and answer data)
      csvContent.push('=== VOTING RESULTS ===');
      
      if (formattedResults.length > 0) {
        
        // Group results by instance for better organization
        const instanceGroups = new Map<string, any[]>();
        
        // Group all results by instanceId
        formattedResults.forEach(result => {
          if (!instanceGroups.has(result.instanceId)) {
            instanceGroups.set(result.instanceId, []);
          }
          instanceGroups.get(result.instanceId)?.push(result);
        });
        
        // Process each instance group
        for (const [instanceId, results] of instanceGroups) {
          // Group results by question within each instance
          const questionGroups = new Map<string, any[]>();
          
          results.forEach(result => {
            if (!questionGroups.has(result.questionId)) {
              questionGroups.set(result.questionId, []);
            }
            questionGroups.get(result.questionId)?.push(result);
          });
          
          // For each question in this instance...
          for (const [questionId, questionResults] of questionGroups) {
            if (questionResults.length === 0) continue;
            
            const instance = questionResults[0].instanceDescription;
            const question = questionResults[0].questionText;
            const totalVotes = questionResults[0].totalVotes;
            
            // Add header row for each new instance/question block
            csvContent.push('Instance,Question,Option,Votes,Total Votes');
            
            // Add each option as a row
            questionResults.forEach(result => {
              csvContent.push(`${escapeCSV(instance)},${escapeCSV(question)},${escapeCSV(result.optionText)},${result.votes},`);
            });
            
            // Add total votes in last row of the question group
            const lastIndex = csvContent.length - 1;
            csvContent[lastIndex] = csvContent[lastIndex] + totalVotes;
            
            // Add a blank line between questions
            csvContent.push('');
          }
        }
      } else {
        csvContent.push('No results available');
      }
      
      // Join all rows with newlines to create final CSV content
      const finalCSVContent = csvContent.join('\n');
      
      // Create a sanitized filename
      const eventName = eventData.name ? eventData.name.replace(/[^a-zA-Z0-9]/g, '_') : eventId;
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `${eventName}_${dateStr}_export.csv`;
      
      // Create and trigger download using the browser's native download capabilities
      const blob = new Blob([finalCSVContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      //alert(`Event data has been downloaded as ${fileName}`);
    } catch (error) {
      console.error("Error downloading event data:", error);
      alert('Error downloading event data. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    api
      .get("/admin/events")
      .then((response) => {
        const data = response.data
        const archivedEvents = data.filter(
          (event: EventSummary) => event.archiveStatus
        )
        setEvents(archivedEvents)
      })
      .catch((error) => {
        console.error("Error fetching active events:", error)
      })
  }, [])

  return (
    <>
      <Typography variant="h6" m={4}>
        Archived Events
      </Typography>
      <Paper elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Event Name</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>View Results</TableCell>
              <TableCell>Download Results</TableCell>
              <TableCell>Delete Archive</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => {
              const startDate = convertToDate(event.startDate)
              const endDate = convertToDate(event.endDate)

              return (
                <TableRow key={event.id}>
                  <TableCell>{event.name}</TableCell>
                  <TableCell>{startDate.toLocaleDateString()}</TableCell>
                  <TableCell>{endDate.toLocaleDateString()}</TableCell>
                  <TableCell>{event.id}</TableCell>
                  <TableCell>
                    <Link
                      to={`/admin/app-admin/event/${event.id}/archived-information`}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ marginRight: "10px" }}
                      >
                        <ForwardIcon />
                      </Button>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ marginRight: "10px" }}
                      onClick={() => downloadEventData(event.id)}
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="secondary"
                      aria-label="Delete"
                      onClick={() => handleDelete(event.id)}
                    >
                      <DeleteIcon />
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Paper>
      {/* Start of Delete single event */}
      <Dialog
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this event?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteConfirmationOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ViewArchivesPage
